import { useCallback, useEffect, useMemo, useState } from "react";
import TdLink from "../../components/TdLink";
import { NavLink } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
  faArrowsRotate,
} from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import {
  getAllChargingPoints,
  deleteChargingPoint,
} from "../../api/ChargingPoint";
import { post_RemoteManagement_reset } from "../../api/Ocpp";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import enabledBadge from "../../components/CheckMarkBadge";
import ConnectorStatusBadge from "../../components/connector/ConnectorStatusBadge";
import DataTable from "../../components/DataTable";
import { useTranslation } from "react-i18next";

const PointsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [points, setPoints] = useState([]);
  const [search, setSearch] = useState("");
  const [pointsLoaded, setPointsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const [resetModal, setResetModal] = useState({
    isOpen: false,
    chargingPointPublicId: null,
    chargingPointName: "",
  });

  const handleGetPoints = useCallback(async () => {
    try {
      const accessToken = await getAccessToken();
      const newPoints = await getAllChargingPoints(accessToken);
      setPoints(Array.isArray(newPoints) ? newPoints : []);
      setPointsLoaded(true);
    } catch (error) {
      console.error(error);
      setPointsLoaded(true);
    }
  }, [getAccessToken]);

  useEffect(() => {
    handleGetPoints();
    const intervalId = setInterval(handleGetPoints, 10000);
    return () => clearInterval(intervalId);
  }, [handleGetPoints]);

  const handleResetPoint = useCallback(async (chargingPointPublicId) => {
    const toastId = toast.loading(t("toast.restartingCharger"));

    try {
      const accessToken = await getAccessToken();
      await post_RemoteManagement_reset(accessToken, {
        chargingPointPublicId,
        type: "Hard",
      });

      toast.success(t("toast.restartCommandSent"), { id: toastId });
    } catch (error) {
      console.error(error);

      const message =
        error instanceof Error ? error.message : t("toast.restartFailed");
      toast.error(message, { id: toastId });
    }
  }, [getAccessToken, t]);


  const handleDeletePoint = useCallback(async (chargingPointPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.point") }));
    try {
      const accessToken = await getAccessToken();
      await deleteChargingPoint(chargingPointPublicId, accessToken);
      await handleGetPoints();
      toast.success(t("toast.deleted", { entity: t("entity.point") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.point") }), { id: toastId });
      console.error(error);
    }
  }, [getAccessToken, handleGetPoints, t]);

  const columns = useMemo(() => [
    {
      key: "chargingPointPublicId",
      header: t("table.pointId"),
      sortable: true,
      rawCell: true,
      getSortValue: (point) => point.chargingPointPublicId ?? "",
      render: (point) => (
        <TdLink to={`/point/${point.chargingPointPublicId}`} className="flex">
          {point.chargingPointPublicId || ""}
        </TdLink>
      ),
      },
      {
          key: "chargingSiteName",
          header: t("entity.site"),
          sortable: true,
          rawCell: true,
          getSortValue: (point) => point.chargingSiteName ?? "",
          render: (point) => (
              <TdLink to={`/point/${point.chargingPointPublicId}`}>
                  {point.chargingSiteName || ""}
              </TdLink>
          ),
      },
    {
      key: "chargingPointName",
      header: t("common.name"),
      sortable: true,
      rawCell: true,
      getSortValue: (point) => point.chargingPointName ?? "",
      render: (point) => (
        <TdLink to={`/point/${point.chargingPointPublicId}`}>
          {point.chargingPointName || ""}
        </TdLink>
      ),
    },
    {
      key: "isOnline",
      header: t("common.online"),
      sortable: true,
      rawCell: true,
      getSortValue: (point) => (point.isOnline ? "true" : "false"),
      render: (point) => (
        <TdLink to={`/point/${point.chargingPointPublicId}`}>
          {enabledBadge(point.isOnline)}
        </TdLink>
      ),
    },
    {
      key: "status",
      header: t("common.status"),
      sortable: true,
      rawCell: true,
      getSortValue: (point) => point.status ?? "",
      render: (point) => (
        <TdLink to={`/point/${point.chargingPointPublicId}`}>
          <ConnectorStatusBadge status={point.status} />
        </TdLink>
      ),
      },
    {
      key: "actions",
      header: "",
      sortable: false,
      searchable: false,
      rawCell: true,
      render: (point) => (
        <td className="flex justify-end">
          <CustomDropdown
            isRight={true}
            ContentComponent={() => (
              <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                <NavLink
                  className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                  to={`/point/${point.chargingPointPublicId}`}
                >
                  <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                </NavLink>

                {point.isOnline && (
                  <>
                    <button
                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                      onClick={() =>
                        setResetModal({
                          isOpen: true,
                          chargingPointPublicId: point.chargingPointPublicId,
                          chargingPointName: point.chargingPointName || point.chargingPointPublicId || "",
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faArrowsRotate} /> {t("common.restart")}
                    </button>
                  </>
                )}
                <button
                  className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                  onClick={() => {
                    setConfirmState({
                      isOpen: true,
                      title: t("confirm.deleteEntity", {
                        entity: t("entity.point"),
                        name: point.chargingPointName,
                      }),
                      confirm: () => {
                        handleDeletePoint(point.chargingPointPublicId);
                      },
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                </button>
              </div>
            )}
          >
            <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
              <FontAwesomeIcon icon={faEllipsis} />
            </div>
          </CustomDropdown>
        </td>
      ),
    },
  ], [handleDeletePoint, t]);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.points")} loading={!pointsLoaded} />
            </div>

            <div className={`${!pointsLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">
                  {t("pages.chargingPoints")}
                </h2>
                <NavLink className="btn btn-primary" to={`/point/create`}>
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createPoint")}
                </NavLink>
              </div>

              <div className="rounded-md shadow-md w-full p-4">
                <DataTable
                  rows={points}
                  columns={columns}
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder={t("search.byIdOrName")}
                  defaultSortColumn="chargingPointPublicId"
                  rowKey={(point, index) => point.chargingPointPublicId || index}
                  emptyColSpan={6}
                />
              </div>
            </div>
          </main>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
      <ConfirmationModal
        isOpen={resetModal.isOpen}
        title={t("confirm.restartEntity", { name: resetModal.chargingPointName })}
        onClose={() =>
          setResetModal({
            isOpen: false,
            chargingPointPublicId: null,
            chargingPointName: "",
          })
        }
        onConfirm={() => {
          const chargingPointPublicId = resetModal.chargingPointPublicId;

          setResetModal({
            isOpen: false,
            chargingPointPublicId: null,
            chargingPointName: "",
          });

          if (chargingPointPublicId) {
            handleResetPoint(chargingPointPublicId);
          }
        }}
      />
    </>
  );
};

export default PointsPage;

